import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import SEO, { serviceSchema } from '../../components/SEO';
import {
  ArrowRight,
  Sparkles,
  ScanFace,
  Image as ImageIcon,
  Share2,
  Download,
  Heart,
  Send,
  Instagram,
  MessageCircle,
  Music2,
  Shield,
  ShieldCheck,
  BarChart3,
  Users,
  Zap,
  Fingerprint,
  ChevronRight,
  Target,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { phoneMockups, heroMosaic } from '../../mock';
import { getLangFromPath, localizePath } from '../../i18n';

/* ---------- Phone frame reused ---------- */
function PhoneFrame({ children, className = '' }) {
  return (
    <div className={`relative mx-auto w-[220px] md:w-[240px] ${className}`}>
      <div className="relative aspect-[9/19] rounded-[2.5rem] bg-gradient-to-b from-[#1a1a24] to-[#0a0a0f] p-2 border border-white/10 shadow-2xl shadow-black/70">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-b-2xl z-30" />
        <div className="relative w-full h-full rounded-[2rem] overflow-hidden bg-[#0a0a0f]">
          {children}
        </div>
      </div>
      <div className="absolute -inset-6 -z-10 bg-violet-500/12 blur-3xl rounded-full" />
    </div>
  );
}

/* ---------- Phone 1 : Selfie ---------- */
function ScreenSelfie() {
  return (
    <>
      <img src={phoneMockups.personA} alt="selfie" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/50" />
      <div className="absolute top-[26%] left-1/2 -translate-x-1/2 w-[62%] aspect-square">
        <span className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-white rounded-tl-md" />
        <span className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-white rounded-tr-md" />
        <span className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-white rounded-bl-md" />
        <span className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-white rounded-br-md" />
      </div>
      <div className="absolute bottom-8 left-0 right-0 flex justify-center">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 p-1">
          <div className="w-full h-full rounded-full bg-white/95" />
        </div>
      </div>
    </>
  );
}

/* ---------- Phone 2 : Branded album (Campari-style) ---------- */
function ScreenBrandedAlbum() {
  const { t } = useTranslation();
  return (
    <>
      <div className="absolute inset-0 bg-white" />
      {/* Brand header */}
      <div className="absolute top-8 left-0 right-0 px-3 flex items-center gap-2">
        <div className="w-9 h-9 rounded-full border-2 border-red-500 p-0.5">
          <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-[8px] font-black text-red-600">
            IMP
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold text-black leading-tight flex items-center gap-1">
            8000xImperial <span className="text-blue-500 text-[10px]">✓</span>
          </div>
          <div className="text-[9px] text-black/60 leading-tight truncate">{t('mockups.brands.brandBadge')}</div>
        </div>
      </div>

      {/* Video row */}
      <div className="absolute top-[22%] left-3 right-3">
        <div className="text-[9px] font-semibold text-black mb-1">{t('mockups.brands.video')}</div>
        <div className="flex gap-1.5">
          {[phoneMockups.album3, phoneMockups.album4, phoneMockups.album5].map((s, i) => (
            <div key={i} className="w-1/3 aspect-square rounded-md overflow-hidden bg-black relative">
              <img src={s} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-4 h-4 rounded-full bg-white/90 flex items-center justify-center">
                  <div className="w-0 h-0 border-l-[4px] border-l-black border-y-[3px] border-y-transparent ml-0.5" />
                </div>
              </div>
              <div className="absolute top-0.5 left-0.5 text-[7px] text-white font-semibold">0:30</div>
            </div>
          ))}
        </div>
      </div>

      {/* Photos grid */}
      <div className="absolute top-[46%] left-3 right-3">
        <div className="text-[9px] font-semibold text-black mb-1">{t('mockups.brands.photos')}</div>
        <div className="grid grid-cols-2 gap-1">
          {[phoneMockups.album1, phoneMockups.album2, phoneMockups.album6, phoneMockups.personB].map((s, i) => (
            <div key={i} className="aspect-square rounded-md overflow-hidden relative">
              <img src={s} alt="" className="w-full h-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 h-3 bg-red-600 flex items-center justify-center">
                <span className="text-[6px] font-black text-white italic">Imperial</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* ---------- Phone 3 : Share with social icons ---------- */
function ScreenShareContacts() {
  const contacts = [
    { name: 'Hugo', img: phoneMockups.album2, social: 'msg', color: 'bg-blue-500' },
    { name: 'Laura', img: phoneMockups.album3, social: 'wa', color: 'bg-emerald-500' },
    { name: 'Ana', img: phoneMockups.album4, social: 'ig', color: 'bg-gradient-to-br from-fuchsia-500 to-orange-500' },
    { name: 'Jasper', img: phoneMockups.album5, social: 'tt', color: 'bg-black border border-white/40' },
  ];
  const iconOf = { wa: MessageCircle, ig: Instagram, msg: Send, tt: Music2 };
  return (
    <>
      <img src={phoneMockups.personB} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/30" />

      {/* Contacts card */}
      <div className="absolute left-3 right-3 top-[38%] rounded-2xl bg-white/95 backdrop-blur-md p-3 shadow-xl">
        <div className="flex items-center justify-between gap-1">
          {contacts.map((c) => {
            const Icon = iconOf[c.social];
            return (
              <div key={c.name} className="flex flex-col items-center gap-1">
                <div className="relative">
                  <div className="w-9 h-9 rounded-full overflow-hidden border border-black/10">
                    <img src={c.img} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ${c.color} flex items-center justify-center shadow`}>
                    <Icon className="w-2.5 h-2.5 text-white" />
                  </div>
                </div>
                <div className="text-[8px] text-black font-medium leading-tight text-center max-w-[36px]">
                  {c.name}<br />Coll..
                </div>
              </div>
            );
          })}
          <div className="flex flex-col items-center gap-1">
            <div className="w-9 h-9 rounded-full bg-white border border-black/10 flex items-center justify-center">
              <ChevronRight className="w-4 h-4 text-black" />
            </div>
          </div>
        </div>
      </div>

      {/* Brand strip bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-red-600 flex items-center justify-center">
        <span className="text-white font-black italic text-sm">Imperial</span>
      </div>
    </>
  );
}

/* ---------- Album with actions (Chandon-style) ---------- */
function BrandedAlbumBig() {
  return (
    <div className="relative w-full max-w-[440px] mx-auto">
      {/* Brand ring */}
      <div className="absolute -top-4 -left-2 z-20 flex items-center gap-3">
        <div className="relative w-14 h-14">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-fuchsia-500 via-red-500 to-amber-400 p-[2px]">
            <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-[9px] font-black text-red-600">
              IMP
            </div>
          </div>
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-blue-500 border-2 border-white flex items-center justify-center text-white text-[8px]">
            ✓
          </div>
        </div>
        <div className="hidden sm:block h-3 flex-1 rounded-full bg-white/10 w-40" />
      </div>

      {/* Grid */}
      <div className="grid grid-cols-3 gap-2 mt-8">
        <div className="col-span-2 row-span-2 aspect-square rounded-2xl overflow-hidden border border-white/10">
          <img src={phoneMockups.personA} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="aspect-square rounded-2xl overflow-hidden border border-white/10">
          <img src={phoneMockups.album2} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="aspect-square rounded-2xl overflow-hidden border border-white/10">
          <img src={phoneMockups.album3} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="aspect-square rounded-2xl overflow-hidden border border-white/10">
          <img src={phoneMockups.album5} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="aspect-square rounded-2xl overflow-hidden border border-violet-400/40 bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 flex items-center justify-center relative">
          <img src={phoneMockups.album4} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="relative text-white font-display font-bold text-lg">+16</div>
        </div>
      </div>

      {/* Floating actions bar */}
      <div className="absolute bottom-[-20px] left-1/2 -translate-x-1/2 flex items-center gap-2 bg-[#0a0a0f]/90 backdrop-blur-md border border-white/10 rounded-full p-1.5 shadow-2xl">
        {[
          { Icon: Heart, bg: 'bg-white/10' },
          { Icon: Download, bg: 'bg-white/10' },
          { Icon: Share2, bg: 'bg-blue-500' },
          { Icon: Sparkles, bg: 'bg-gradient-to-br from-violet-400 to-fuchsia-500' },
        ].map(({ Icon, bg }, i) => (
          <div key={i} className={`w-9 h-9 rounded-full ${bg} flex items-center justify-center text-white`}>
            <Icon className="w-4 h-4" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Lead form mockup (middle pillar) ---------- */
function LeadFormMockup() {
  const { t } = useTranslation();
  return (
    <div className="relative w-full flex items-end gap-2 justify-center">
      {/* Left phone (smaller) */}
      <div className="w-[110px] rounded-2xl bg-white shadow-xl overflow-hidden border border-white/20 pb-2">
        <div className="p-2">
          <div className="text-[7px] font-black text-black uppercase mb-1.5 leading-tight">{t('mockups.brands.fillYours')}</div>
          <div className="space-y-1">
            <div className="text-[6px] text-black/60">{t('mockups.common.name')}</div>
            <div className="h-2 rounded bg-blue-100" />
            <div className="text-[6px] text-black/60">{t('mockups.common.lastName')}</div>
            <div className="h-2 rounded bg-blue-100" />
            <div className="text-[6px] text-black/60">{t('mockups.common.email')}</div>
            <div className="h-2 rounded bg-blue-100" />
          </div>
        </div>
      </div>
      {/* Right phone (bigger) */}
      <div className="w-[130px] rounded-2xl bg-white shadow-xl overflow-hidden border border-white/20 pb-2 relative -translate-y-2">
        <div className="p-2">
          <div className="text-[7px] font-black text-black uppercase mb-1.5 leading-tight">{t('mockups.brands.fillThose')}</div>
          <div className="space-y-1">
            <div className="text-[6px] text-black/60">{t('mockups.common.name')}</div>
            <div className="h-2 rounded bg-blue-100 flex items-center px-1"><span className="text-[6px] text-black">Juan</span></div>
            <div className="text-[6px] text-black/60">{t('mockups.common.email')}</div>
            <div className="h-2 rounded bg-blue-100 flex items-center px-1"><span className="text-[6px] text-black">juan@marca.com</span></div>
            <div className="text-[6px] text-black/60">{t('mockups.common.company')}</div>
            <div className="h-2 rounded bg-blue-100 flex items-center px-1"><span className="text-[6px] text-black">Imperial</span></div>
            <div className="pt-1 flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-sm bg-blue-500" />
              <span className="text-[6px] text-black/70">{t('mockups.common.acceptTerms')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Dashboard mockup (bottom) ---------- */
function BrandDashboard() {
  const { t } = useTranslation();
  return (
    <div className="relative w-full">
      <div className="rounded-2xl bg-white text-black p-4 shadow-2xl">
        {/* Fake browser */}
        <div className="flex items-center gap-1 mb-3">
          <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
          <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          <div className="ml-3 text-[9px] text-black/50">dashboard.momentia.shop</div>
        </div>

        {/* Top KPIs */}
        <div className="flex items-center gap-2 mb-3">
          <div className="font-display font-bold text-black">{t('mockups.brands.statsTitle')}</div>
          <div className="ml-auto text-[9px] text-black/50">{t('mockups.brands.statsPeriod')}</div>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="p-2 rounded-lg border border-black/8">
            <div className="text-[9px] text-black/50">{t('mockups.brands.checkins')}</div>
            <div className="text-lg font-display font-bold text-black">2.128</div>
            <div className="text-[9px] text-black/50">{t('mockups.brands.statsAccept')}</div>
          </div>
          <div className="p-2 rounded-lg border border-black/8">
            <div className="text-[9px] text-black/50">{t('mockups.brands.photosAvg')}</div>
            <div className="text-lg font-display font-bold text-black">13</div>
            <div className="text-[9px] text-black/50">{t('mockups.brands.photosAvgSub')}</div>
          </div>
          <div className="p-2 rounded-lg border border-black/8">
            <div className="text-[9px] text-black/50">{t('mockups.brands.engagement')}</div>
            <div className="text-lg font-display font-bold text-black">23%</div>
            <div className="text-[9px] text-black/50">{t('mockups.brands.engagementSub')}</div>
          </div>
        </div>

        {/* Chart */}
        <div className="p-3 rounded-lg border border-black/8">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-semibold text-black">{t('mockups.brands.checkinsByDay')}</div>
            <div className="flex items-center gap-1">
              <div className="px-1.5 py-0.5 rounded bg-violet-500 text-white text-[8px] font-semibold">{t('mockups.brands.tabDay')}</div>
              <div className="px-1.5 py-0.5 rounded text-black/50 text-[8px]">{t('mockups.brands.tabWeek')}</div>
              <div className="px-1.5 py-0.5 rounded text-black/50 text-[8px]">{t('mockups.brands.tabMonth')}</div>
            </div>
          </div>
          <div className="flex items-end justify-between gap-1 h-16">
            {[35, 60, 45, 80, 55, 90, 70, 85, 50, 75, 40, 88, 65].map((h, i) => (
              <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-violet-400 to-fuchsia-500" style={{ height: `${h}%` }} />
            ))}
          </div>
        </div>

        {/* Extra rows: clicks / cupones / descargas */}
        <div className="grid grid-cols-3 gap-2 mt-3">
          <div className="p-2 rounded-lg border border-black/8">
            <div className="text-[9px] text-black/50 flex items-center gap-1">{t('mockups.brands.clicks')}</div>
            <div className="text-base font-display font-bold text-black leading-none mt-1">18.462</div>
            <div className="text-[8px] text-emerald-600 mt-0.5 font-semibold">{t('mockups.brands.clicksVs')}</div>
          </div>
          <div className="p-2 rounded-lg border border-black/8">
            <div className="text-[9px] text-black/50 flex items-center gap-1">{t('mockups.brands.coupons')}</div>
            <div className="text-base font-display font-bold text-black leading-none mt-1">2.184</div>
            <div className="text-[8px] text-emerald-600 mt-0.5 font-semibold">{t('mockups.brands.conv18')}</div>
          </div>
          <div className="p-2 rounded-lg border border-black/8">
            <div className="text-[9px] text-black/50 flex items-center gap-1">{t('mockups.brands.downloads')}</div>
            <div className="text-base font-display font-bold text-black leading-none mt-1">6.842</div>
            <div className="text-[8px] text-emerald-600 mt-0.5 font-semibold">{t('mockups.brands.downloadsVs')}</div>
          </div>
        </div>
      </div>

      {/* Floating badges */}
      <div className="absolute -top-3 -left-3 px-2 py-1 rounded-full bg-white border border-black/8 text-[10px] font-semibold text-black shadow-lg flex items-center gap-1">
        😊 <span>{t('mockups.brands.badgeCheckins')}</span>
      </div>
      <div className="absolute -bottom-3 -right-3 px-2 py-1 rounded-full bg-white border border-black/8 text-[10px] font-semibold text-black shadow-lg flex items-center gap-1">
        🔥 <span>{t('mockups.brands.badgeShares')}</span>
      </div>
    </div>
  );
}

/* ==============================================================
   PAGE
============================================================== */

/* Auto-cycling carousel between MomentIA generic mockups and Campari real case */
const MOCKUP_SETS = [
  {
    label: 'Modelo · Music Experience',
    images: [
      'https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/iuudqgdq_MOCKUP%20APP%20WEB%20MOMENTIA-01.png',
      'https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/9bnp4ezf_MOCKUP%20APP%20WEB%20MOMENTIA-02.png',
      'https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/ks6plcm1_MOCKUP%20APP%20WEB%20MOMENTIA-03.png',
    ],
    glow: 'rgba(217,70,239,0.25)',
  },
  {
    label: 'Campari · Red Edition',
    images: [
      '/mockups/corporate-b2b-1.png',
      '/mockups/corporate-b2b-2.png',
      '/mockups/corporate-b2b-3.png',
    ],
    glow: 'rgba(220,38,38,0.28)',
  },
];

function BrandsMockupsCarousel() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((v) => (v + 1) % MOCKUP_SETS.length), 4000);
    return () => clearInterval(t);
  }, []);

  const current = MOCKUP_SETS[idx];

  return (
    <div className="relative">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-6">
        {current.images.map((src, i) => (
          <div
            key={`${idx}-${i}`}
            className="relative flex justify-center brands-mockup-fade"
            style={{ animationDelay: `${i * 0.08}s` }}
          >
            <img
              src={src}
              alt={`${current.label} mockup ${i + 1}`}
              className="w-full max-w-[280px] h-auto object-contain"
              style={{ filter: `drop-shadow(0 25px 50px ${current.glow})` }}
              loading="lazy"
            />
          </div>
        ))}
      </div>

      {/* Set label + dots */}
      <div className="flex items-center justify-center gap-4 mt-8">
        <div className="text-xs uppercase tracking-[0.25em] text-white/60 font-medium">
          {current.label}
        </div>
        <div className="flex gap-2">
          {MOCKUP_SETS.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Ver set ${i + 1}`}
              className={`w-8 h-1 rounded-full transition-all ${
                i === idx ? 'bg-white' : 'bg-white/20 hover:bg-white/40'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Brands() {
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const to = (p) => localizePath(p, lang);
  const steps = t('services.brands.howItWorks.steps', { returnObjects: true }) || [];
  const starHighlights = t('services.brands.star.highlights', { returnObjects: true }) || [];
  const measureTags = t('services.brands.measure.tags', { returnObjects: true }) || [];
  const useCases = t('services.brands.useCases.items', { returnObjects: true }) || [];
  return (
    <>
      <SEO
        title={t('services.brands.seoTitle')}
        description={t('services.brands.seoDescription')}
        path="/servicios/marcas"
        schema={serviceSchema({ name: 'Fotos con IA para Marcas y Activaciones BTL', description: 'Plataforma de fotos con reconocimiento facial para activaciones de marca, sampling y experiencias branded.', path: '/servicios/marcas' })}
      />
      {/* HERO */}
      <section className="relative overflow-hidden pt-16 lg:pt-20 pb-16">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 -left-32 w-[500px] h-[500px] bg-violet-600/15 rounded-full blur-[140px]" />
          <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-fuchsia-500/12 rounded-full blur-[160px]" />
        </div>
        <div className="container-mi relative">
          <div className="max-w-3xl">
            <Link to={to('/')} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-violet-300 mb-5 hover:text-violet-200 transition-colors">
              <span>{t('services.brands.breadcrumb')}</span><span>/</span><span className="text-white/60">{t('services.brands.breadcrumbCurrent')}</span>
            </Link>
            <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-[1.02]">
              {t('services.brands.heroTitle1')}
              <span className="block bg-gradient-to-r from-violet-300 via-violet-400 to-fuchsia-300 bg-clip-text text-transparent">
                {t('services.brands.heroTitleHighlight')}
              </span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
              {t('services.brands.heroSubtitle')}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to={to('/request-demo')}>
                <Button className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-6 rounded-full font-semibold">
                  {t('services.brands.ctaDemo')} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to={to('/precios')}>
                <Button variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                  {t('services.brands.ctaPricing')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS - carousel between MomentIA generic mockups and Campari real case */}
      <section className="py-20 lg:py-24 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3">{t('services.brands.howItWorks.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('services.brands.howItWorks.titleLine1')}
              <span className="block text-white/50">{t('services.brands.howItWorks.titleLine2')}</span>
            </h2>
          </div>

          <BrandsMockupsCarousel />

          <div className="grid md:grid-cols-3 gap-10 mt-10 max-w-5xl mx-auto">
            {[
              { n: 1, color: 'bg-blue-500' },
              { n: 2, color: 'bg-gradient-to-br from-pink-500 to-fuchsia-500' },
              { n: 3, color: 'bg-fuchsia-500' },
            ].map((s, i) => (
              <div key={s.n} className="flex flex-col items-center text-center">
                <div className={`w-8 h-8 rounded-full ${s.color} flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
                  {s.n}
                </div>
                <h3 className="font-display text-lg md:text-xl font-semibold mt-3 mb-2">{steps[i]?.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed max-w-xs">{steps[i]?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MAXIMIZE MARKETING INVESTMENT */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3 flex items-center gap-2">
                <Target className="w-3.5 h-3.5" /> {t('services.brands.maximize.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                <span className="bg-gradient-to-r from-violet-300 to-fuchsia-300 bg-clip-text text-transparent">{t('services.brands.maximize.titleHighlight')}</span> {t('services.brands.maximize.titleLine1')}
                <br />
                {t('services.brands.maximize.titleLine2')}
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                {t('services.brands.maximize.description')}
              </p>
              <div className="mt-8 grid grid-cols-3 gap-6">
                <div>
                  <div className="font-display text-3xl font-bold text-white">42%</div>
                  <div className="text-xs text-white/50 mt-1">{t('services.brands.maximize.stats.shareRate')}</div>
                </div>
                <div>
                  <div className="font-display text-3xl font-bold text-white">3x</div>
                  <div className="text-xs text-white/50 mt-1">{t('services.brands.maximize.stats.reach')}</div>
                </div>
                <div>
                  <div className="font-display text-3xl font-bold text-white">18d</div>
                  <div className="text-xs text-white/50 mt-1">{t('services.brands.maximize.stats.lifetime')}</div>
                </div>
              </div>
            </div>

            {/* Right: sponsored event photo composition */}
            <div className="grid grid-cols-2 gap-3">
              <div className="aspect-square rounded-2xl overflow-hidden border border-white/10 relative group">
                <img
                  src="https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/6zj9el48_IMAGENES%20CORPORATIVAS%20DE%20EVENTOS_Mesa%20de%20trabajo%201.webp"
                  alt="Runner 21k La Plata con branding Powerade y New Balance"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute bottom-2 left-2 px-2 py-1 rounded-md bg-blue-600 text-white text-[9px] font-bold">
                  POWERADE · 21K
                </div>
              </div>
              <div className="aspect-square rounded-2xl overflow-hidden border border-white/10 relative group">
                <img
                  src="https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/jjl6kfh0_IMAGENES%20CORPORATIVAS%20DE%20EVENTOS_Mesa%20de%20trabajo%201%20copia%203.webp"
                  alt="Torneo de golf con activación Modelo Especial"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute bottom-2 left-2 px-2 py-1 rounded-md bg-blue-900 text-white text-[9px] font-bold">
                  MODELO · GOLF PRO-AM
                </div>
              </div>
              <div className="aspect-square rounded-2xl overflow-hidden border border-white/10 relative group">
                <img
                  src="https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/tzt5qloc_IMAGENES%20CORPORATIVAS%20DE%20EVENTOS_Mesa%20de%20trabajo%201%20copia%204.webp"
                  alt="Networking corporativo con branding Movistar y Volkswagen"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute bottom-2 left-2 px-2 py-1 rounded-md bg-sky-600 text-white text-[9px] font-bold">
                  MOVISTAR · VW
                </div>
              </div>
              <div className="aspect-square rounded-2xl overflow-hidden border border-white/10 relative group">
                <img
                  src="https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/ozdtd3jb_IMAGENES%20CORPORATIVAS%20DE%20EVENTOS_Mesa%20de%20trabajo%201%20copia%206.webp"
                  alt="Cena branded Michelob Ultra"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute bottom-2 left-2 px-2 py-1 rounded-md bg-indigo-900 text-white text-[9px] font-bold">
                  MICHELOB ULTRA
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEEL LIKE A STAR */}
      <section className="py-24 lg:py-28 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 pt-8">
              <BrandedAlbumBig />
            </div>
            <div className="order-1 lg:order-2">
              <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3">{t('services.brands.star.eyebrow')}</div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                {t('services.brands.star.titleLine1')}
                <br />
                {t('services.brands.star.titleLine2Prefix')} <span className="bg-gradient-to-r from-violet-300 to-fuchsia-300 bg-clip-text text-transparent">{t('services.brands.star.titleHighlight')}</span>
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                {t('services.brands.star.description')}
              </p>
              <ul className="mt-8 space-y-3 text-white/80">
                {starHighlights.map((txt) => (
                  <li key={txt} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-violet-500/20 border border-violet-400/30 flex items-center justify-center">
                      <Sparkles className="w-3 h-3 text-violet-300" />
                    </div>
                    {txt}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 3 PILLARS */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3">{t('services.brands.pillars.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
              {t('services.brands.pillars.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {/* Card 1 - White/Light */}
            <div className="rounded-3xl bg-[#111116] border border-white/10 p-7 flex flex-col">
              <h3 className="font-display text-2xl font-bold mb-3">
                {t('services.brands.pillars.brand.titlePrefix')} <span className="text-violet-300">{t('services.brands.pillars.brand.titleHighlight')}</span> {t('services.brands.pillars.brand.titleSuffix')}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed mb-6 flex-1">
                {t('services.brands.pillars.brand.description')}
              </p>
              {/* Mini brand album preview */}
              <div className="mt-auto">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full border-2 border-red-500 flex items-center justify-center text-[8px] font-black text-red-600 bg-white">
                    IMP
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold text-white flex items-center gap-1">Imperial · Fiesta 26 <span className="text-blue-400">✓</span></div>
                    <div className="text-[9px] text-white/50">@imperial · #EnTuMomento</div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-1">
                  {[phoneMockups.album1, phoneMockups.album2, phoneMockups.album3, phoneMockups.album4].map((s, i) => (
                    <div key={i} className="aspect-square rounded overflow-hidden">
                      <img src={s} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Card 2 - Blue highlighted */}
            <div className="rounded-3xl bg-gradient-to-br from-blue-600 to-blue-700 p-7 flex flex-col text-white shadow-2xl shadow-blue-900/40">
              <h3 className="font-display text-2xl font-bold mb-3">
                {t('services.brands.pillars.leads.titleLine1')}
                <br />
                {t('services.brands.pillars.leads.titleLine2')}
              </h3>
              <p className="text-white/85 text-sm leading-relaxed mb-6 flex-1">
                {t('services.brands.pillars.leads.description')}
              </p>
              <div className="mt-auto">
                <LeadFormMockup />
              </div>
            </div>

            {/* Card 3 - Privacy */}
            <div className="rounded-3xl bg-[#111116] border border-white/10 p-7 flex flex-col">
              <h3 className="font-display text-2xl font-bold mb-3">
                <span className="text-violet-300">{t('services.brands.pillars.privacy.titleHighlight')}</span> {t('services.brands.pillars.privacy.titleSuffix')}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed mb-6 flex-1">
                {t('services.brands.pillars.privacy.description')}
              </p>
              <div className="mt-auto flex items-center justify-center gap-4 py-4">
                <div className="relative">
                  <div className="w-20 h-24 rounded-xl bg-gradient-to-b from-blue-500 to-blue-600 flex items-center justify-center relative">
                    <ShieldCheck className="w-10 h-10 text-white" />
                    <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-emerald-500 border-2 border-[#111116] flex items-center justify-center text-white">
                      <span className="text-lg leading-none">✓</span>
                    </div>
                  </div>
                  <div className="text-center mt-2 text-[10px] font-bold text-white">GDPR</div>
                </div>
                <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                  <Fingerprint className="w-9 h-9 text-violet-300" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MEASURE SUCCESS */}
      <section className="py-24 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-500/20 via-fuchsia-500/15 to-orange-500/20 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-fuchsia-500/20 rounded-full blur-[160px]" />
        <div className="container-mi relative">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/70 mb-3 flex items-center gap-2">
                <BarChart3 className="w-3.5 h-3.5" /> {t('services.brands.measure.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight text-white">
                {t('services.brands.measure.titleLine1')}
                <br />
                {t('services.brands.measure.titleLine2')}
              </h2>
              <p className="mt-5 text-lg text-white/85 leading-relaxed max-w-xl">
                {t('services.brands.measure.description')}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {measureTags.map((tag) => (
                  <span key={tag} className="px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-sm text-white">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <BrandDashboard />
            </div>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="py-24 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3">{t('services.brands.useCases.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
              {t('services.brands.useCases.title')}
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {useCases.map((u) => (
              <div key={u} className="px-5 py-3 rounded-full border border-white/10 bg-[#111116] text-white/80 text-sm hover:border-violet-400/40 hover:bg-violet-500/5 hover:text-white transition-colors">
                {u}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container-mi">
          <div className="relative overflow-hidden rounded-3xl border border-violet-400/20 bg-gradient-to-br from-[#141425] via-[#101018] to-[#0e0e14] p-10 md:p-14 text-center">
            <div className="absolute -top-32 -right-24 w-[500px] h-[500px] bg-violet-500/15 rounded-full blur-[100px]" />
            <div className="relative">
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
                {t('services.brands.finalCta.title')}
              </h2>
              <p className="mt-4 text-white/70 max-w-xl mx-auto">
                {t('services.brands.finalCta.subtitle')}
              </p>
              <div className="mt-8 flex justify-center gap-3 flex-wrap">
                <Link to={to('/request-demo')}>
                  <Button className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-7 rounded-full font-semibold">
                    {t('services.brands.finalCta.cta')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
